import React from 'react'
import styled from 'styled-components'
import {
    Avatar,
    Button,
    Card,
    CardContent,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import PhoneInput from 'components/Control/PhoneInput.js'
import useOrganizationCard, {
    MEMBER_BY_EMAIL,
    MEMBER_BY_PHONE,
} from 'logic/Organizations/useOrganizationCard.js'

const getMemberName = ({firstName, lastName, email}) =>
    [firstName, lastName].filter(Boolean).join(' ') || email

const OrganizationCard = ({organization, t, showSaved, showError}) => {
    const {
        isEditing,
        name,
        isSaving,
        startEditing,
        cancelEditing,
        handleNameChange,
        handleSave,
        memberBy,
        email,
        phoneCountry,
        phoneNumber,
        phoneError,
        isAddingMember,
        handleMemberByChange,
        handleEmailChange,
        handlePhoneCountryChange,
        handlePhoneNumberChange,
        handleAddMember,
        handleRemoveMember,
    } = useOrganizationCard({organization, showSaved, showError})

    return (
        <StyledCard>
            <CardContent>
                {isEditing ? (
                    <Row as="form" onSubmit={handleSave}>
                        <TextField
                            size="small"
                            label={t('name')}
                            value={name}
                            onChange={handleNameChange}
                            autoFocus
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSaving || !name.trim()}>
                            {t('save')}
                        </Button>
                        <Button onClick={cancelEditing}>{t('cancel')}</Button>
                    </Row>
                ) : (
                    <Row>
                        <Typography variant="h6">
                            {organization.name}
                        </Typography>
                        <Tooltip title={t('edit')}>
                            <IconButton onClick={startEditing}>
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    </Row>
                )}

                <Typography variant="subtitle2" sx={{mt: 2}}>
                    {t('members')} ({organization.users.length})
                </Typography>
                <List dense>
                    {organization.users.map((member) => (
                        <ListItem
                            key={member._id}
                            secondaryAction={
                                <Tooltip title={t('remove')}>
                                    <IconButton
                                        edge="end"
                                        onClick={() =>
                                            handleRemoveMember(member._id)
                                        }>
                                        <PersonRemoveIcon />
                                    </IconButton>
                                </Tooltip>
                            }>
                            <ListItemAvatar>
                                <Avatar src={member.profileImageUrl} />
                            </ListItemAvatar>
                            <ListItemText
                                primary={getMemberName(member)}
                                secondary={member.email}
                            />
                        </ListItem>
                    ))}
                </List>

                <Typography variant="subtitle2">{t('addMember')}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {t('addMemberHint')}
                </Typography>
                <AddMemberForm onSubmit={handleAddMember} noValidate>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={memberBy}
                        onChange={handleMemberByChange}>
                        <ToggleButton value={MEMBER_BY_EMAIL}>
                            {t('byEmail')}
                        </ToggleButton>
                        <ToggleButton value={MEMBER_BY_PHONE}>
                            {t('byPhone')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                    {memberBy === MEMBER_BY_EMAIL ? (
                        <EmailTextField
                            type="email"
                            label={t('email')}
                            value={email}
                            onChange={handleEmailChange}
                            inputProps={{dir: 'ltr'}}
                        />
                    ) : (
                        <PhoneInput
                            name={`phone-${organization._id}`}
                            country={phoneCountry}
                            number={phoneNumber}
                            onCountryChange={handlePhoneCountryChange}
                            onNumberChange={handlePhoneNumberChange}
                            countryLabel={t('phoneCountry')}
                            numberLabel={t('phone')}
                            error={phoneError}
                            helperText={phoneError && t('invalidPhone')}
                        />
                    )}
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isAddingMember}>
                        {t('addMemberButton')}
                    </Button>
                </AddMemberForm>
            </CardContent>
        </StyledCard>
    )
}

export default OrganizationCard

const StyledCard = styled(Card)`
    && {
        width: 500px;

        @media (max-width: 600px) {
            width: 100%;
        }
    }
`

const Row = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`

const AddMemberForm = styled.form`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin-top: 10px;
`

const EmailTextField = styled(TextField)`
    && {
        width: 450px;

        @media (max-width: 600px) {
            width: 250px;
        }
    }
`
